import { EmptyState } from "@/components/EmptyState";
import { ChatIcon } from "@/components/icons";
import { useQuery } from "@tanstack/react-query";
import { cn } from "cn";

import { formatDate } from "../lib/dates.ts";

import {
  conversationsQuery,
  namedTitle,
  UNTITLED_CONVERSATION,
  type ConversationSummary,
} from "../chat/conversations.ts";
import { ConversationActions } from "./ConversationActions.tsx";
import { SIDEBAR_TEXTS } from "./texts.ts";

interface Props {
  readonly currentId: string | undefined;
  onOpen: (conversationId: string) => void;
  /** Se ha borrado una conversación; puede ser la que está abierta. */
  onDeleted: (conversationId: string) => void;
}

export function ConversationList({ currentId, onOpen, onDeleted }: Props) {
  const { data: conversations = [], isError } = useQuery(conversationsQuery());

  return (
    // El scroll es de la lista: el armazón ya no scrollea por ella, porque la
    // memoria necesita dos scrolls independientes y no uno común.
    <div className="grid min-h-0 flex-1 content-start gap-1 overflow-y-auto">
      {isError && (
        <p className="text-sm text-muted-foreground">
          {SIDEBAR_TEXTS.historyFailed}
        </p>
      )}

      {!isError && conversations.length === 0 && (
        <EmptyState icon={ChatIcon}>{SIDEBAR_TEXTS.noConversations}</EmptyState>
      )}

      <ul className="grid gap-1">
        {conversations.map((conversation) => (
          <li key={conversation.id}>
            <ConversationRow
              conversation={conversation}
              current={conversation.id === currentId}
              onOpen={onOpen}
              onDeleted={onDeleted}
            />
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * Una fila del historial: abrir por un lado, las acciones por el otro.
 *
 * La fila entera FUE un solo `<button>`, que es lo natural cuando lo único que
 * se puede hacer es abrirla. Dejó de serlo al añadir el menú: un botón dentro
 * de otro botón no es HTML válido, el navegador deshace el anidamiento por su
 * cuenta y lo que queda no se puede usar ni con el teclado ni con un lector de
 * pantalla. Ahora el contenedor solo pinta --el resaltado y el `group` del que
 * cuelga la aparición del menú-- y dentro hay dos controles hermanos.
 */
function ConversationRow({
  conversation,
  current,
  onOpen,
  onDeleted,
}: {
  readonly conversation: ConversationSummary;
  readonly current: boolean;
  onOpen: (conversationId: string) => void;
  onDeleted: (conversationId: string) => void;
}) {
  // Una conversación recién empezada todavía no tiene nombre: lo pone un
  // modelo en el backend y llega un momento después. Se dice igual que en la
  // cabecera para que las dos hablen de lo mismo.
  const title = namedTitle(conversation.title);

  return (
    <div
      className={cn(
        "group flex items-center rounded-md transition-colors",
        "hover:bg-secondary",
        current && "bg-secondary",
      )}
    >
      {/* `aria-current` sigue en el control que abre, no en el contenedor: lo
          que está "en curso" es esta conversación, y quien la abre es él.
          `min-w-0` para que el truncado del título funcione dentro del flex:
          sin eso el nombre largo estira la fila y empuja el menú fuera. */}
      <button
        type="button"
        aria-current={current ? "true" : undefined}
        onClick={() => {
          onOpen(conversation.id);
        }}
        className="grid min-w-0 flex-1 gap-0.5 rounded-md px-2.5 py-2 text-left"
      >
        <span
          className={cn(
            "truncate text-sm font-medium",
            title === null && "font-normal text-muted-foreground",
          )}
        >
          {title ?? UNTITLED_CONVERSATION}
        </span>
        <span className="truncate text-xs text-muted-foreground">
          {relativeDate(conversation.updatedAt)} ·{" "}
          {conversation.messageCount === 1
            ? SIDEBAR_TEXTS.oneMessage
            : SIDEBAR_TEXTS.messages(conversation.messageCount)}
        </span>
      </button>

      <ConversationActions conversation={conversation} onDeleted={onDeleted} />
    </div>
  );
}

/**
 * Fecha en la escala en la que la gente piensa en sus chats: hoy, ayer, el día
 * de la semana, y ya después la fecha.
 */
const relativeDate = (iso: string): string => {
  const date = new Date(iso);
  const days = Math.floor((Date.now() - date.getTime()) / 86_400_000);

  if (days <= 0) {
    return date.toLocaleTimeString("es", {
      hour: "2-digit",
      minute: "2-digit",
    });
  }
  if (days === 1) return SIDEBAR_TEXTS.yesterday;
  if (days < 7) return date.toLocaleDateString("es", { weekday: "long" });
  return formatDate(date);
};
